import type { ErrorInfo, ReactNode } from 'react';
import type React from 'react';
import { Component } from 'react';
import { Container, Title, Text, Button, Stack, Paper, Center } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs errors, and displays a fallback UI instead of crashing the whole app.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * Note: Error boundaries only catch errors in:
 * - Rendering
 * - Lifecycle methods
 * - Constructors of components below them
 *
 * They do NOT catch errors in:
 * - Event handlers (use try/catch)
 * - Asynchronous code (use try/catch)
 * - Server-side rendering
 * - Errors thrown in the error boundary itself
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Always log errors to console for visibility in all environments
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Optional: Send error to logging service
    // logErrorToService(error, errorInfo);
  }

  handleReset = (): void => {
    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call optional reset handler
    if (this.props.onReset) {
      this.props.onReset();
    } else {
      // Default: reload the page
      window.location.href = '/';
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallbackTitle, fallbackMessage } = this.props;
      const { error } = this.state;

      return (
        <Center style={{ minHeight: '100vh', padding: '2rem' }}>
          <Container size="sm">
            <Paper shadow="md" p="xl" radius="md" withBorder>
              <Stack gap="md" align="center">
                <IconAlertTriangle size={64} color="red" />

                <Title order={2} ta="center">
                  {fallbackTitle || 'Ups! Coś poszło nie tak'}
                </Title>

                <Text size="md" c="dimmed" ta="center">
                  {fallbackMessage ||
                    'Wystąpił nieoczekiwany błąd. Przepraszamy za niedogodności. Nasz zespół został powiadomiony o problemie.'}
                </Text>

                {/* Show error details in development */}
                {import.meta.env.DEV && error && (
                  <Paper p="sm" bg="gray.1" radius="sm" style={{ width: '100%' }}>
                    <Text size="sm" c="red" fw={600} mb="xs">
                      Szczegóły błędu (widoczne tylko w trybie rozwojowym):
                    </Text>
                    <Text size="xs" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {error.toString()}
                    </Text>
                  </Paper>
                )}

                <Stack gap="sm" style={{ width: '100%' }}>
                  <Button
                    fullWidth
                    size="lg"
                    onClick={this.handleReset}
                    variant="filled"
                  >
                    Wróć do strony głównej
                  </Button>

                  <Button
                    fullWidth
                    size="md"
                    variant="subtle"
                    onClick={() => window.location.reload()}
                  >
                    Odśwież stronę
                  </Button>
                </Stack>

                <Text size="xs" c="dimmed" ta="center">
                  Jeśli problem się powtarza, skontaktuj się z nami: pomoc@taryfypradu.pl
                </Text>
              </Stack>
            </Paper>
          </Container>
        </Center>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap any component with an ErrorBoundary
 *
 * Usage:
 * ```tsx
 * const SafeComponent = withErrorBoundary(MyComponent);
 * ```
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallbackTitle?: string,
  fallbackMessage?: string
) {
  return function WithErrorBoundaryComponent(props: P) {
    return (
      <ErrorBoundary fallbackTitle={fallbackTitle} fallbackMessage={fallbackMessage}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}
